'use client';

import { DatasetPipelineList, DatasetPipelineStatus, TaskTypeEnum } from '@api';
import { useApi } from '@data-access';
import { ErrorHelper } from '@helpers';
import { DotsHorizontalIcon } from '@radix-ui/react-icons';
import { Button } from '@registry/new-york/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@registry/new-york/ui/dropdown-menu';
import { Row, Table } from '@tanstack/react-table';
import { AppRoutes } from '@types';
import { useRouter } from 'next/router';
import { toast } from 'react-toastify';

interface DataTableRowActionsProps<TData> {
  row: Row<TData>;
  table: Table<TData>;
}

export const DataTableRowActions = <TData,>({ row, table }: DataTableRowActionsProps<TData>) => {
  const { datasetPiplineApi } = useApi();
  const { push } = useRouter();
  const pipeline = row.original as DatasetPipelineList;
  const isConcierge = pipeline?.taskType === TaskTypeEnum.concierge;
  const isAssessmentDisabled =
    isConcierge || pipeline?.status !== DatasetPipelineStatus.completed || pipeline?.dataset?.hasPredictions !== true;

  const redirectToAssessment = () => {
    if (isAssessmentDisabled) {
      return;
    }

    push({
      pathname: AppRoutes.dataCollection,
      query: {
        datasetPipeline: pipeline?.id,
        ...(pipeline?.aiModel?.id && { aiModelId: pipeline.aiModel.id }),
      },
    });
  };

  const deleteDataset = async () => {
    const { id } = row.original as DatasetPipelineList;
    try {
      await datasetPiplineApi.apiDatasetPipelinesDestroy({ id });
      toast.success('Dataset successfully deleted', { autoClose: 6000 });
      (table as any).options.meta.removeRow(id);
    } catch (error) {
      ErrorHelper.handleErrorNotification(error);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="flex h-8 w-8 p-0 data-[state=open]:bg-muted"
          id="actions"
          data-testid="row-actions-button"
        >
          <DotsHorizontalIcon className="h-4 w-4" />
          <span className="sr-only">Open menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-[160px]"
      >
        <DropdownMenuItem
          disabled={isAssessmentDisabled}
          onSelect={() => redirectToAssessment()}
          data-testid="start-assessment-action"
        >
          Start Assessment
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={isConcierge}
          onSelect={() => push({ pathname: AppRoutes.datasetInventoryAdd, query: { from: pipeline.id } })}
          data-testid="recreate-dataset-action"
        >
          Recreate dataset
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => deleteDataset()}
          data-testid="delete-dataset-action"
          disabled={pipeline.hasAssessments}
        >
          Delete dataset
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
